import { useLanguage } from '../contexts/LanguageContext';
import { TimeSlotPicker } from './TimeSlotPicker';
import { Clock, Copy, Plus, X } from 'lucide-react';

const DAYS = [
  { key: 'monday', labelKey: 'calendar.monday' },
  { key: 'tuesday', labelKey: 'calendar.tuesday' },
  { key: 'wednesday', labelKey: 'calendar.wednesday' },
  { key: 'thursday', labelKey: 'calendar.thursday' },
  { key: 'friday', labelKey: 'calendar.friday' },
  { key: 'saturday', labelKey: 'calendar.saturday' },
  { key: 'sunday', labelKey: 'calendar.sunday' },
];

const MAX_INTERVALS = 3;
const CLOSED_WORDS = ['lukket', 'closed', 'geschlossen', 'lukke'];

/** Parse a single "08:00-17:00" segment into { open, close } */
const parseSegment = (val) => {
  if (!val || typeof val !== 'string') return { open: '', close: '' };
  const norm = val.trim().replace(/\.+$/, '').replace(/\./g, ':').replace(/[–—]/g, '-');
  const parts = norm.split('-', 2);
  const open = (parts[0] || '').trim();
  let close = (parts[1] || '').trim();
  if (!close || close === '??') close = '?';
  return { open, close };
};

/**
 * Parse stored value (single string per day) into an array of intervals.
 * "08:00-12:00, 14:00-17:00" → [{open:"08:00",close:"12:00"},{open:"14:00",close:"17:00"}]
 * "Lukket" / "" → []
 */
const parseStoredIntervals = (val) => {
  if (!val || typeof val !== 'string') return [];
  const low = val.trim().toLowerCase();
  if (!low || CLOSED_WORDS.includes(low)) return [];
  const segments = val.split(',').map(s => s.trim()).filter(Boolean);
  return segments.map(parseSegment).filter(iv => iv.open);
};

/** Convert array of intervals back to storage string */
const intervalsToStored = (intervals) => {
  if (!Array.isArray(intervals) || intervals.length === 0) return '';
  return intervals
    .filter(iv => iv && iv.open)
    .map(iv => {
      const close = iv.close && iv.close !== '?' ? iv.close : '?';
      return `${iv.open}-${close}`;
    })
    .join(', ');
};

export const OpeningHoursEditor = ({ openingHours, onChange, onSave, saving }) => {
  const { t } = useLanguage();
  const hours = openingHours || {};

  const updateDay = (dayKey, intervals) => {
    onChange({ ...hours, [dayKey]: intervalsToStored(intervals) });
  };

  const handleIntervalChange = (dayKey, idx, field, value) => {
    const intervals = parseStoredIntervals(hours[dayKey]);
    intervals[idx] = { ...intervals[idx], [field]: value };
    updateDay(dayKey, intervals);
  };

  const addInterval = (dayKey) => {
    const intervals = parseStoredIntervals(hours[dayKey]);
    if (intervals.length >= MAX_INTERVALS) return;
    intervals.push({ open: '14:00', close: '17:00' });
    updateDay(dayKey, intervals);
  };

  const removeInterval = (dayKey, idx) => {
    const intervals = parseStoredIntervals(hours[dayKey]);
    intervals.splice(idx, 1);
    updateDay(dayKey, intervals);
  };

  const toggleDay = (dayKey) => {
    const intervals = parseStoredIntervals(hours[dayKey]);
    if (intervals.length > 0) {
      onChange({ ...hours, [dayKey]: '' });
    } else {
      onChange({ ...hours, [dayKey]: '08:00-22:00' });
    }
  };

  const copyToAll = (sourceDayKey) => {
    const val = hours[sourceDayKey];
    if (!val) return;
    const updated = {};
    DAYS.forEach(d => { updated[d.key] = val; });
    onChange(updated);
  };

  return (
    <div className="space-y-2" data-testid="opening-hours-editor">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4 text-emerald-400" />
        <span className="text-white font-semibold text-sm">{t('openingHoursEditor.title') || 'Åbningstider'}</span>
      </div>

      {DAYS.map(({ key, labelKey }) => {
        const intervals = parseStoredIntervals(hours[key]);
        const isOpen = intervals.length > 0;
        const canAddMore = intervals.length < MAX_INTERVALS;

        return (
          <div key={key} className="space-y-1.5" data-testid={`oh-day-${key}`}>
            <div className="flex items-start gap-2">
              {/* Day toggle */}
              <button
                type="button"
                onClick={() => toggleDay(key)}
                className={`w-20 shrink-0 py-1.5 rounded-lg text-xs font-semibold transition-all border text-center ${
                  isOpen
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-zinc-800 text-zinc-500 border-zinc-700'
                }`}
                data-testid={`oh-toggle-${key}`}
              >
                {t(labelKey)}
              </button>

              {!isOpen && (
                <span className="text-zinc-500 text-xs italic mt-1.5">
                  {t('openingHoursEditor.closed') || 'Lukket'}
                </span>
              )}

              {/* Intervals stack */}
              {isOpen && (
                <div className="flex-1 space-y-1.5">
                  {intervals.map((iv, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`oh-interval-${key}-${idx}`}>
                      <TimeSlotPicker
                        value={iv.open}
                        onChange={(v) => handleIntervalChange(key, idx, 'open', v)}
                        testId={`oh-open-${key}-${idx}`}
                      />
                      <span className="text-zinc-500 text-xs">—</span>
                      <TimeSlotPicker
                        value={iv.close}
                        onChange={(v) => handleIntervalChange(key, idx, 'close', v)}
                        allowOpenEnded={true}
                        testId={`oh-close-${key}-${idx}`}
                      />
                      {/* Remove this interval (only if more than 1) */}
                      {intervals.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInterval(key, idx)}
                          className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title={t('openingHoursEditor.removeInterval') || 'Fjern interval'}
                          data-testid={`oh-remove-${key}-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {/* Copy-to-all only on first interval */}
                      {idx === 0 && (
                        <button
                          type="button"
                          onClick={() => copyToAll(key)}
                          className="p-1.5 text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-colors"
                          title={t('openingHoursEditor.copyToAll') || 'Kopiér til alle dage'}
                          data-testid={`oh-copy-${key}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}

                  {/* Add new interval */}
                  {canAddMore && (
                    <button
                      type="button"
                      onClick={() => addInterval(key)}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold text-emerald-400 hover:bg-emerald-500/10 rounded-lg border border-emerald-500/30 border-dashed transition-colors"
                      data-testid={`oh-add-${key}`}
                    >
                      <Plus className="w-3 h-3" />
                      {t('openingHoursEditor.addInterval') || 'Tilføj interval'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {onSave && (
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="w-full mt-2 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg transition-colors disabled:opacity-50 text-sm"
          data-testid="oh-save-btn"
        >
          {saving ? '...' : t('openingHoursEditor.save') || 'Gem åbningstider'}
        </button>
      )}
    </div>
  );
};

export default OpeningHoursEditor;
